import { useCallback } from 'react';
import { connect } from 'react-redux';

import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmFormRef,
  getKnmIsErknmStatusTypeWaitingCarryOut,
  getKnmIsNeedActSaveOrSign,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { SelectInputFieldAutoOptions } from '../../../../UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { CommonCatalogsNamesEnum } from '../../../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { getUserRoleIsSupervisor } from '../../../../AuthPackage/MainModule/selectors/authSelectors';
import { FieldBlock } from '../../../../UIPackage/FormModule/components/FieldBlock';
import { Button } from '../../../../UIPackage/MainModule/components/Button';
import { TextInputField } from '../../../../UIPackage/MainModule/components/TextInputField';
import { ABROGATION_REASON_ID_BLOCK_ID } from '../../../../KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmAbrogationForm } from '../../../../APIPackage/KnmModule/interfaces/IKnmAbrogationForm';
import { ErknmReasonsCancelledDocument } from '../../../../KnmErknmPackage/ErknmReasonsModule/components/ErknmReasonsCancelledDocument';
import { ErknmReasonCancelledLink } from '../../../../KnmErknmPackage/ErknmReasonsModule/components/ErknmReasonCancelledLink';
import { IKnmAbrogationReason } from '../../../../APIPackage/CatalogsModule/interfaces/IKnmAbrogationReason';
import styles from './PmAbrogaion.module.scss';
import { CloseButton } from '../../../../UIPackage/MainModule/components/CloseButton';

interface IPmAbrogationProps {
  abrogation: IKnmAbrogationForm | null | undefined;
  isWatchOnly: boolean;
}

interface IPmAbrogationStoreProps {
  formRef: IKnmFormRef | undefined;
  isKnmStatusTypeWaitingCarryOut: boolean;
  isSupervisor: boolean;
  isNeedActSaveOrSign?: boolean;
}

const PmAbrogationWithoutStore = ({
  abrogation,
  isWatchOnly,
  formRef,
  isKnmStatusTypeWaitingCarryOut,
  isSupervisor,
  isNeedActSaveOrSign = false,
}: IPmAbrogationProps & IPmAbrogationStoreProps) => {
  /**
   * Функция добавления сведений об отмене
   */
  const addAbrogation = useCallback(() => {
    formRef?.current.change('abrogation', {});
  }, [formRef]);

  const removeAbrogation = useCallback(() => formRef?.current.change('abrogation'), [formRef]);

  /**
   * Функция очистки формы отмены мероприятия при изменении причины отмены
   */
  const handleApproveRequiredSelectChange = useCallback(() => {
    formRef?.current.change('dataContent');
    formRef?.current.change('approved');
    formRef?.current.change('approveNumberDecision');
    formRef?.current.change('approveDateDecision');
    formRef?.current.change('approveFioSigner');
    formRef?.current.change('approveTitleSigner');
    formRef?.current.change('approveAppeal');
    formRef?.current.change('approveNoteAppeal');
    formRef?.current.change('rejectReasonType');
    formRef?.current.change('approveDateRejectReason');
  }, [formRef]);

  /**
   * Функция фильтрации значений для поля "Выберите сведение"
   */
  const abrogationReasonsTransformFunc = useCallback((items: IKnmAbrogationReason[]) => {
    return !items ? [] : items.filter(item => item.pm);
  }, []);

  return (
    <FieldBlock
      label="Сведения об отмене мероприятия"
      button={
        !abrogation && (
          <Button
            className="shared-btn-add"
            text="Добавить"
            type="button"
            variant="link"
            color="primary"
            onClick={addAbrogation}
            disabled={!isSupervisor || !!abrogation || !isKnmStatusTypeWaitingCarryOut || isNeedActSaveOrSign}
          />
        )
      }
    >
      {abrogation && (
        <>
          <FieldBlock label="Основание для отмены" color="black" placementLabel="left">
            <SelectInputFieldAutoOptions
              id={ABROGATION_REASON_ID_BLOCK_ID}
              name="abrogation.reasonId"
              className={styles.ReasonCancelKNM}
              disabled={isWatchOnly || !isKnmStatusTypeWaitingCarryOut}
              optionName={CommonCatalogsNamesEnum.KNM_TYPES_CANCELED}
              onChange={handleApproveRequiredSelectChange}
              transformFunc={abrogationReasonsTransformFunc}
            />
            <CloseButton className="shared-deleting-row-btn" onClick={removeAbrogation} />
          </FieldBlock>
          <FieldBlock label="Комментарий" color="black" placementLabel="left" margin="top">
            <TextInputField
              id="abrogation.comment"
              name="abrogation.comment"
              className={styles.ReasonCancelKNM}
              disabled={isWatchOnly || !isKnmStatusTypeWaitingCarryOut || isNeedActSaveOrSign}
              size="medium"
            />
          </FieldBlock>
          <FieldBlock margin="top">
            <ErknmReasonsCancelledDocument
              title={'Файл основания для отмены'}
              isWatchOnly={isWatchOnly || !isKnmStatusTypeWaitingCarryOut || isNeedActSaveOrSign}
              disableToAdd={isWatchOnly || !isKnmStatusTypeWaitingCarryOut || isNeedActSaveOrSign}
              className={styles.ReasonCancelKNMFile}
              name={`abrogation.reasonFile`}
              formRef={formRef}
            />
            {(abrogation?.reasonId?.id === 1 || abrogation?.reasonId?.id === 2) && (
              <ErknmReasonCancelledLink
                watchOnly={isWatchOnly || !isKnmStatusTypeWaitingCarryOut || isNeedActSaveOrSign}
                isEnabled={true}
                abrogation={abrogation}
              />
            )}
          </FieldBlock>
        </>
      )}
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IPmAbrogationStoreProps => ({
  formRef: getKnmFormRef(state),
  isKnmStatusTypeWaitingCarryOut: getKnmIsErknmStatusTypeWaitingCarryOut(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  isNeedActSaveOrSign: getKnmIsNeedActSaveOrSign(state),
});

export const PmAbrogation = connect(mapStateToProps)(PmAbrogationWithoutStore);
